import React, { useCallback, useContext, useRef, useState } from 'react'
import { Button, NotificationManagerContext, Select, Spacer,
   Text, Textarea, View, ZoomProvider } from 'oio-react'
import { Link } from 'react-router-dom'
import ContainerLayout1 from 'src/components/Home/ContainerLayout1'
import ContainerLayout2 from 'src/components/Home/ContainerLayout2'
import ContainerLayout3 from 'src/components/Home/ContainerLayout3'
import HtmlEditor from 'src/components/utils/HtmlEditor'
import { useDebounced, useFileUploads } from 'src/components/utils'
import ImageUpload from 'src/components/utils/ImageUpload'
import ScrollView from 'src/components/utils/ScrollView'
import FuseHomeLayout from 'src/custom/fuse/components/HomeLayout'
import { useOrganization, useUpdateOrganization } from 'src/graphql/hooks'
import { Theme } from 'src/libraries/contexts/Theme'

// =======================================================
// Container Layouts
// =======================================================

const containerLayouts = [
   null,
   ContainerLayout1,
   ContainerLayout2,
   ContainerLayout3,
   FuseHomeLayout
]

// =======================================================
// Component
// =======================================================

// TODO: Use Form instead of managing form data state
const HomeEdit = () => {
   const { organization } = useOrganization()
   const { updateOrganization, mutating } = useUpdateOrganization()
   const { showNotification } = useContext(NotificationManagerContext)

   const { uploadFile, uploadedFiles, uploadsInProgress, resetFileUploadState } =
      useFileUploads({ targetType: 'organization' })

   const descriptionInputRef = useRef(null)
   const initialDescription = organization.description
   const [formData, setFormData] = useState({
      description: organization.description,
      homeContainerLayoutKey: organization.homeContainerLayoutKey,
      tagline: organization.tagline
   })

   const defaultLayout = containerLayouts[1]
   const ContainerLayout = containerLayouts[formData.homeContainerLayoutKey] || defaultLayout

   // Debounced form updating
   const debouncedUpdateFormField = useDebounced((name, value) => {
      setFormData({
         ...formData,
         [name]: value
      })
   })

   const updateFormField = useCallback((e) => {
      const field = e.currentTarget
      let { value } = field

      if (field.name === 'homeContainerLayoutKey') {
         value = parseInt(field.value, 10)
      }

      debouncedUpdateFormField(field.name, value)
   })

   const handleDescriptionChange = useCallback(() => {
      const description = descriptionInputRef.current.editor.html.get(true)

      setFormData({
         ...formData,
         description
      })
   })

   const save = useCallback(() => {
      updateOrganization({
         ...uploadedFiles,
         ...formData
      })
      .then((response) => {
         showNotification({
            message: 'Changes saved successfully',
            title: 'Success!',
            type: 'success'
         })

         resetFileUploadState()
      })
      .catch(() => {
         showNotification({
            message: 'There was an error while saving',
            title: 'Error!',
            type: 'error'
         })
      })
   })

   return (
      <>
         <View
            position="fixed"
            top="0px"
            left="0px"
            right="0px"
            bottom="0px">
            <View
               display="flex"
               alignItems="center"
               justifyContent="space-between"
               position="absolute"
               top="0px"
               left="0px"
               height="72px"
               right="0px"
               padding="0px 30px"
               backgroundColor="#1f2329">
               <Text size="3" weight="medium" color="#fff">
                  Home Edit
               </Text>
               <View>
                  <Link to="/">
                     <Button
                        color="#888"
                        size="sm"
                        name="Close"
                        outline
                     />
                  </Link>
                  <Spacer size="1" orientation="vertical" />
                  <Button
                     onClick={save}
                     color={(mutating || uploadsInProgress.length > 0) ? '#111' : '#F0CB9E'}
                     mode={(mutating || uploadsInProgress.length > 0) ? 'loading' : 'normal'}
                     textColor="#000"
                     size="sm"
                     name="Save Changes"
                  />
               </View>
            </View>
            <View
               display="flex"
               justifyContent="center"
               position="absolute"
               top="72px"
               left="0px"
               bottom="0px"
               right="330px"
               backgroundColor="#ccc"
               paddingBottom="120px"
               scroll="on">
               <ZoomProvider zoom={0.6}>
                  <View
                     position="relative"
                     float="left"
                     width="100vw"
                     paddingTop="60px"
                     backgroundColor="#fff">
                     <ContainerLayout
                        organizationOverride={formData}
                     />
                  </View>
               </ZoomProvider>
            </View>
            <ScrollView
               position="absolute"
               top="72px"
               right="0px"
               bottom="0px"
               width="330px"
               backgroundColor="#fff"
               padding="30px"
               zIndex="2"
               scroll="on">
               <ImageUpload
                  onFileChange={uploadFile}
                  name="coverMedia"
                  purpose="coverPhoto"
                  src={organization.coverMedia?.file.thumbnailUrlW480}
               />
               <View float="left" width="100%" height="12px" />
               {formData.homeContainerLayoutKey < 3 && (
                  <>
                     <Select
                        onChange={updateFormField}
                        label="Layout"
                        name="homeContainerLayoutKey">
                        <option value="1" selected={formData.homeContainerLayoutKey === 1}>
                           Layout 1
                        </option>
                        <option value="2" selected={formData.homeContainerLayoutKey === 2}>
                           Layout 2
                        </option>
                     </Select>
                     <View float="left" width="100%" height="12px" />
                  </>
               )}
               <Textarea
                  onChange={updateFormField}
                  label="Tagline"
                  name="tagline"
                  defaultValue={formData.tagline}
               />
               <View float="left" width="100%" height="12px" />
               <Text size="2">
                  <HtmlEditor
                     ref={descriptionInputRef}
                     config={{ heightMin: 150 }}
                     onChange={handleDescriptionChange}
                     initialValue={initialDescription}
                  />
               </Text>
            </ScrollView>
         </View>
         <Theme
            viewportMarginTop="0px"
            viewportMarginLeft="0px"
            globalNavIsHidden
         />
      </>
   )
}

export default HomeEdit
